import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Control,
  Controller,
  UseFormRegister,
  useWatch,
  UseFormTrigger,
  useFormContext,
} from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Select, MenuItem, FormGroup, Typography } from "@mui/material";
import {
  SectionHeader,
  SectionTitle,
  EditButton,
  CancelButton,
  InputContainer,
  CustomInput,
  FormLabel,
  ButtonContainerStyled,
  AvailabilityInputContainer,
  ErrorBox,
  RelativeContainer,
  CheckboxGroupContainer,
  DayRowContainer,
  DayInputGroup,
  ZeroMarginFormControlLabel,
  StyledCustomSelect,
  WideAvailabilityInputContainer,
  FixedWidthFormControlLabel,
} from "../styles";
import CustomSelect from "@Common/CustomSelect";
import { IPropertyAvailabilitySettings } from "../types";
import {
  HOURS_OPTIONS,
  PREPARATION_TIME_OPTIONS,
  generateDaysOptions,
  generateDaysOfWeekOptions,
} from "@utils";

// Static options moved to utils/property-options.ts

interface AvailabilitySettingsSectionProps {
  availabilitySettings?: {
    max_days_notice?: number;
    booking_lead_time?: number;
    turnover_days?: number;
    default_max_nights?: number;
    default_min_nights?: number;
    day_of_week_check_in?: boolean[];
    day_of_week_check_out?: boolean[];
    day_of_week_min_nights?: number[];
  };
  register: UseFormRegister<IPropertyAvailabilitySettings>;
  control: Control<IPropertyAvailabilitySettings>;
  onSave: () => void;
  reset: () => void;
  trigger: UseFormTrigger<IPropertyAvailabilitySettings>;
}

const AvailabilitySettingsSection: React.FC<AvailabilitySettingsSectionProps> = React.memo(({
  availabilitySettings,
  register,
  control: propControl,
  onSave,
  reset,
  trigger: propTrigger,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  
  // Use FormProvider context if available, otherwise fall back to props
  const formContext = useFormContext<IPropertyAvailabilitySettings>();
  const control = formContext?.control || propControl;
  const trigger = formContext?.trigger || propTrigger;
  

  // Performance optimization: Memoized options with translations
  const DAYS_OPTIONS = useMemo(() => generateDaysOptions(t), [t]);
  const DAYS_OF_WEEK = useMemo(() => generateDaysOfWeekOptions(t), [t]);

  // Performance optimization: Watch individual fields
  const maxDaysNotice = useWatch({ control, name: "max_days_notice" });
  const bookingLeadTime = useWatch({ control, name: "booking_lead_time" });
  const turnoverDays = useWatch({ control, name: "turnover_days" });
  const defaultMaxNights = useWatch({ control, name: "default_max_nights" });
  const defaultMinNights = useWatch({ control, name: "default_min_nights" });
  const dayOfWeekCheckIn = useWatch({ control, name: "day_of_week_check_in" });
  const dayOfWeekCheckOut = useWatch({ control, name: "day_of_week_check_out" });
  const dayOfWeekMinNights = useWatch({ control, name: "day_of_week_min_nights" });
  

  // Performance optimization: Memoized validation handler
  const handleSave = useCallback(async () => {
    const fieldsToValidate: (keyof IPropertyAvailabilitySettings)[] = [
      "max_days_notice",
      "booking_lead_time",
      "turnover_days", 
      "default_max_nights",
      "default_min_nights",
      "day_of_week_check_in",
      "day_of_week_check_out",
      "day_of_week_min_nights",
    ];

    const isValid = await trigger(fieldsToValidate as any);
    
    if (!isValid) {
      // Force re-render by triggering validation and updating form state
      for (const field of fieldsToValidate) {
        await trigger(field as any);
      }
      // Force component re-render to show errors
      setIsEditing(prev => prev); // This will trigger a state update
    }
    
    if (isValid) {
      onSave();
      setIsEditing(false);
    } 
  }, [maxDaysNotice, bookingLeadTime, turnoverDays, defaultMaxNights, defaultMinNights, dayOfWeekCheckIn, dayOfWeekCheckOut, dayOfWeekMinNights, availabilitySettings, trigger, onSave, setIsEditing]);

  // Performance optimization: Memoized cancel handler
  const handleCancel = useCallback(() => {
    reset();
    setIsEditing(false);
  }, [reset]);

  return (
    <>
      {/* Availability Settings Section Header */}
      <SectionHeader>
        <SectionTitle>{t("AVAILABILITY_SETTINGS.AVAILABILITY_SETTINGS")}</SectionTitle>
        {!isEditing && (
          <EditButton onClick={() => setIsEditing(true)}>
            {t("AVAILABILITY_SETTINGS.EDIT")}
          </EditButton>
        )}
        {isEditing && (
          <ButtonContainerStyled>
            <EditButton onClick={handleCancel}>
              {t("COMMON.CANCEL")}
            </EditButton>
            <CancelButton onClick={handleSave}>
              {t("COMMON.SAVE")}
            </CancelButton>
          </ButtonContainerStyled>
        )}
      </SectionHeader>

      {/* Number of Days Select */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.NUMBER_OF_DAYS")}</FormLabel>
        <Controller
          name="max_days_notice"
          control={control}
          rules={{
            required: t("AVAILABILITY_SETTINGS.THIS_FIELD_IS_REQUIRED"),
          }}
          render={({ field, fieldState: { error } }) => (
            <RelativeContainer>
              <StyledCustomSelect>
                <CustomSelect
                  {...field}
                  value={field.value ? String(field.value) : undefined}
                  onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                  data={DAYS_OPTIONS}
                  disabled={!isEditing}
                />
              </StyledCustomSelect>
              {error && <ErrorBox>{error.message}</ErrorBox>}
            </RelativeContainer>
          )}
        />
      </InputContainer>

      {/* Number of Hours Select */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.NUMBER_OF_HOURS")}</FormLabel>
        <Controller
          name="booking_lead_time"
          control={control}
          rules={{
            required: t("AVAILABILITY_SETTINGS.THIS_FIELD_IS_REQUIRED"),
            min: {
              value: 1,
              message: t("AVAILABILITY_SETTINGS.MINIMUM_1_HOUR_REQUIRED"),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <RelativeContainer>
              <StyledCustomSelect>
                <CustomSelect
                  {...field}
                  value={field.value ? String(field.value) : undefined}
                  onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                  data={HOURS_OPTIONS}
                  disabled={!isEditing}
                />
              </StyledCustomSelect>
              {error && <ErrorBox>{error.message}</ErrorBox>}
            </RelativeContainer>
          )}
        />
      </InputContainer>

      {/* Preparation Time Select */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.PREPARATION_TIME")}</FormLabel>
        <Controller
          name="turnover_days"
          control={control}
          rules={{
            required: t("AVAILABILITY_SETTINGS.THIS_FIELD_IS_REQUIRED"),
          }}
          render={({ field, fieldState: { error } }) => (
            <RelativeContainer>
              <StyledCustomSelect>
                <CustomSelect
                  {...field}
                  value={field.value ? String(field.value) : undefined}
                  onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                  data={PREPARATION_TIME_OPTIONS}
                  disabled={!isEditing}
                />
              </StyledCustomSelect>
              {error && <ErrorBox>{error.message}</ErrorBox>}
            </RelativeContainer>
          )}
        />
      </InputContainer>

      {/* Max Nights Number Input */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.MAX_NIGHTS")}</FormLabel>
        <AvailabilityInputContainer>
          <Controller
            name="default_max_nights"
            control={control}
            rules={{
              required: t("AVAILABILITY_SETTINGS.THIS_FIELD_IS_REQUIRED"),
              min: {
                value: 1,
                message: t("AVAILABILITY_SETTINGS.MINIMUM_1_NIGHT_REQUIRED"),
              },
              max: {
                value: 365,
                message: t("AVAILABILITY_SETTINGS.MAXIMUM_365_NIGHTS_ALLOWED"),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <RelativeContainer>
                <CustomInput
                  {...field}
                  disabled={!isEditing}
                  type="number"
                  value={field.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === "" ? "" : Number(value));
                  }}
                  placeholder={t("AVAILABILITY_SETTINGS.ENTER_MAX_NIGHTS")}
                />
                {error && (
                  <ErrorBox>
                    {error.message}
                  </ErrorBox>
                )}
              </RelativeContainer>
            )}
          />
        </AvailabilityInputContainer>
      </InputContainer>

      {/* Min Nights Number Input */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.MIN_NIGHTS")}</FormLabel>
        <AvailabilityInputContainer>
          <Controller
            name="default_min_nights"
            control={control}
            rules={{
              required: t("AVAILABILITY_SETTINGS.THIS_FIELD_IS_REQUIRED"),
              min: {
                value: 1,
                message: "Minimum 1 night required",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <RelativeContainer>
                <CustomInput
                  {...field}
                  disabled={!isEditing}
                  type="number"
                  value={field.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === "" ? "" : Number(value));
                  }}
                  placeholder={t("AVAILABILITY_SETTINGS.ENTER_MIN_NIGHTS")}
                />
                {error && (
                  <ErrorBox>
                    {error.message}
                  </ErrorBox>
                )}
              </RelativeContainer>
            )}
          />
        </AvailabilityInputContainer>
      </InputContainer>

      {/* Check-in Dates Checkboxes */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.CHECK_IN_DATES")}</FormLabel>
        <Controller
          name="day_of_week_check_in"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <RelativeContainer>
              <FormGroup row>
                {DAYS_OF_WEEK.map((day) => (
                  <FormControlLabel
                    key={day.key}
                    control={
                      <Checkbox
                        disabled={!isEditing}
                        checked={field.value?.[day.index] || false}
                        onChange={(e) => {
                          const newValue = [...(field.value || Array(7).fill(false))];
                          newValue[day.index] = e.target.checked;
                          field.onChange(newValue);
                        }}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
              {error && (
                <ErrorBox>
                  {error.message}
                </ErrorBox>
              )}
            </RelativeContainer>
          )}
        />
      </InputContainer>

      {/* Check-out Dates Checkboxes */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.CHECK_OUT_DATES")}</FormLabel>
        <Controller
          name="day_of_week_check_out"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <RelativeContainer>
              <FormGroup row>
                {DAYS_OF_WEEK.map((day) => (
                  <FormControlLabel
                    key={day.key}
                    control={
                      <Checkbox
                        disabled={!isEditing}
                        checked={field.value?.[day.index] || false}
                        onChange={(e) => {
                          const newValue = [...(field.value || Array(7).fill(false))];
                          newValue[day.index] = e.target.checked;
                          field.onChange(newValue);
                        }}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
              {error && (
                <ErrorBox>
                  {error.message}
                </ErrorBox>
              )}
            </RelativeContainer>
          )}
        />
      </InputContainer>

      {/* Min Nights Per Week */}
      <InputContainer>
        <FormLabel>{t("AVAILABILITY_SETTINGS.MIN_NIGHTS_PER_WEEK")}</FormLabel>
        <Controller
          name="day_of_week_min_nights"
          control={control}
          render={({ field }) => (
            <CheckboxGroupContainer>
              {DAYS_OF_WEEK.map((day) => (
                <DayRowContainer key={day.key}>
                  <DayInputGroup>
                  <FixedWidthFormControlLabel
                    control={
                      <Checkbox
                        id={`min-nights-checkbox-${day.key}`}
                        name={`min_nights_${day.key}_enabled`}
                        disabled={!isEditing}
                        checked={field.value?.[day.index] !== undefined && field.value?.[day.index] > 0}
                        onChange={(e) => {
                          const newValue = [...(field.value || Array(7).fill(0))];
                          newValue[day.index] = e.target.checked ? 1 : 0;
                          field.onChange(newValue);
                        }}
                        />
                    }
                    label= {day.label}
                  />
                    <WideAvailabilityInputContainer>
                      <CustomInput
                        id={`min-nights-input-${day.key}`}
                        name={`min_nights_${day.key}_value`}
                        disabled={!isEditing || !((field.value?.[day.index] ?? 0) > 0)}
                        type="number"
                        value={(field.value?.[day.index] ?? 0) > 0 ? (field.value?.[day.index] ?? 1) : 1}
                        onChange={(e) => {
                          const newValue = [...(field.value || Array(7).fill(0))];
                          const inputValue = Number(e.target.value);
                          newValue[day.index] = inputValue > 0 ? inputValue : 1;
                          field.onChange(newValue);
                        }}
                        placeholder="1"
                        min={1}
                        max={7}
                      />
                    </WideAvailabilityInputContainer>

                  </DayInputGroup>

                </DayRowContainer>
              ))}
            </CheckboxGroupContainer>
          )}
        />
      </InputContainer>
    </>
  );
});

// Performance optimization: Display name for React DevTools
AvailabilitySettingsSection.displayName = "AvailabilitySettingsSection";

export default AvailabilitySettingsSection;
